import { useState, useCallback } from 'react';
import type { ChangeEvent, MouseEvent, FormEvent } from 'react';
import { z } from 'zod';
import { buildStoreGiftCard } from '../../config/storeGiftCard.build';
import { useStoreGiftCard } from './useStoreGiftCard';
import type { GiftCardFormData } from '../../domain/models/storeGiftCard.types';

const storeGiftCardService = buildStoreGiftCard().service;

const storeGiftCardSchema = z
  .object({
    amount: z
      .union([z.number(), z.string()])
      .refine((val) => Number(val) >= 1000 && Number(val) <= 50000, {
        message: 'Amount must be between ₹1,000 and ₹50,000.',
      }),
  });

export type FormFieldKeys = keyof GiftCardFormData;
export type FieldErrorsMap = Partial<Record<FormFieldKeys, string>>;

export const useGiftCardForm = () => {
  const storeGc = useStoreGiftCard(storeGiftCardService);

  const [formData, setFormData] = useState<GiftCardFormData>({
    amount: '',
    pincode: '',
  });

  const [fieldErrors, setFieldErrors] = useState<FieldErrorsMap>({});

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const fieldName = name as FormFieldKeys;

      setFieldErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev));

      if (fieldName === 'pincode') {
        storeGc.resetPincodeState();
      }

      setFormData((prev) => ({
        ...prev,
        [fieldName]: fieldName === 'amount' ? (value ? Number(value) : '') : value,
      }));
    },
    [storeGc]
  );

  const handleAmountSelect = useCallback((val: number) => {
    setFieldErrors((prev) => (prev.amount ? { ...prev, amount: undefined } : prev));
    setFormData((prev) => ({ ...prev, amount: val }));
  }, []);

  const handlePincodeCheckClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const pincode = formData.pincode?.trim() || '';
      if (!/^[1-9][0-9]{5}$/.test(pincode)) {
        setFieldErrors((prev) => ({ ...prev, pincode: 'Please enter a valid pincode.' }));
        return;
      }
      storeGc.checkPincodeManual(pincode);
    },
    [formData.pincode, storeGc]
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setFieldErrors({});

      const result = storeGiftCardSchema.safeParse(formData);

      if (!result.success) {
        const errorMsg = result.error.flatten().fieldErrors.amount?.[0];
        if (errorMsg) {
          setFieldErrors({ amount: errorMsg });
        }
        return;
      }

      storeGc.processOrder(formData);
    },
    [formData, storeGc]
  );

  return {
    ...storeGc,
    formData,
    fieldErrors,
    handleChange,
    handleAmountSelect,
    handlePincodeCheckClick,
    handleSubmit,
  };
};